import React, { useState } from 'react'

import { Box, Button, Grid, Typography } from '@mui/material'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import { GeneratedExampleDropdown } from 'components/GeneratedExampleDropdown/GeneratedExampleDropdown'
import { generatedExampleDropdownData } from 'components/GeneratedExampleDropdown/generatedExampleDropdownData'
import InfoPopper from 'components/InfoPopper/InfoPopper'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import refreshIcon from 'assets/images/refreshIcon.svg'

import { styles } from './Section2.styles'

export const TEST_ID = 'leap-frog-section2'

type Response = {
	ideas: string[]
	title: string
}

type BulletItem = {
	details: string
	title?: string
}

type PopperContent = {
	bulletItems: BulletItem[]
	header: string
	introText: string
}

export type Props = {
	currentPrompts: string[]
	currentResponses: Response[]
	generatePromptsDisabled: () => boolean
	handleAiResponseClick: () => void
	handleRandomPrompts: () => void
	isAiLoading: boolean
	isLoading: boolean
	popperContent: PopperContent
}

const Section2 = ({
	isLoading,
	isAiLoading,
	currentPrompts,
	currentResponses,
	generatePromptsDisabled,
	handleRandomPrompts,
	popperContent,
}: Props) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
	const [businessComparisonIndex, setBusinessComparisonIndex] = useState(0)
	const gameId = GAME_TYPE.LEAP_FROG.value as number
	const gameHeroData = generatedExampleDropdownData[gameId]
	const [showAIExampleIdea, setShowAIExampleIdea] = useState(false)

	const handleClickInfoIcon = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget)
	}

	const buildResponseItem = (solution: Response[]) => {
		return (
			<ol
				data-testid="gen-ai-response-custom-list"
				style={styles.responseList(gameHeroData.bgSecondary)}
			>
				{solution.map((el, i) =>
					i === businessComparisonIndex ? (
						<React.Fragment key={i}>
							{el.ideas.map((subel, x) => (
								<Typography
									component="li"
									data-testid="gen-ai-sub-response-list"
									key={`ai-sub-solution-${x}`}
									sx={styles.responseListItem}
									variant="body1"
								>
									{subel}
								</Typography>
							))}
						</React.Fragment>
					) : null
				)}
			</ol>
		)
	}

	return (
		<>
			<Section2Title gameType="leapFrog" />
			<UserEducationModal />
			<Box sx={styles.rootContainer}>
				<Box sx={styles.sideBar} />
				<Box sx={styles.outerContainer}>
					<Box sx={styles.innerContainer}>
						<Box sx={styles.instructionContainer}>
							<Typography sx={styles.instructionText}>
								How can you draw inspiration from each of the
								random elements below, either individually or in
								combination with each other?
							</Typography>
						</Box>
						<Box sx={styles.contextContainer}>
							<InfoPopper
								handleClick={handleClickInfoIcon}
								anchorEl={anchorEl}
								popperContent={popperContent}
								id={'leap-frog-info-popper'}
							/>
							<Typography
								data-testid={`${TEST_ID}__context-button`}
								sx={styles.contextText}
							>
								CONTEXT
							</Typography>
						</Box>
						<ButtonWithIconComponent
							disabled={generatePromptsDisabled() || isLoading}
							iconSvg={refreshIcon}
							text="REFRESH"
							sx={styles.refreshButton}
							data-testid={`${TEST_ID}__refresh-button`}
							onClickAction={handleRandomPrompts}
						/>
					</Box>
					{isLoading ? (
						<LoadingSpinner />
					) : (
						<Grid item sx={styles.containerAi}>
							{currentPrompts?.map((value, index) => (
								<Button
									sx={styles.businessComparisonButton(
										businessComparisonIndex === index
									)}
									data-testid={`${TEST_ID}__business-comparison-${index}`}
									key={`${TEST_ID}__business-comparison-key-${index}`}
									className={
										index === businessComparisonIndex
											? 'selected'
											: ''
									}
									onClick={() =>
										setBusinessComparisonIndex(index)
									}
								>
									{value}
								</Button>
							))}
						</Grid>
					)}
					<Typography sx={styles.ideasDropdownText}>
						{brainstormText.global.ideasDropdown}
					</Typography>

					<GeneratedExampleDropdown
						isLoading={isAiLoading}
						gameTypeEnum={GAME_TYPE.LEAP_FROG}
						headerText=""
						customResponse={buildResponseItem(currentResponses)}
						showAIExampleIdea={showAIExampleIdea}
						setShowAIExampleIdea={setShowAIExampleIdea}
					/>
				</Box>
			</Box>
		</>
	)
}

export default Section2
